// ============================================================
// MA-ARCH-183B: Shared Brand Token System
// Single source of visual truth for LP + Funnel
//
// Both the landing page (server-rendered) and funnel (React)
// derive their styling from these tokens. The LP resolves
// them in Python (brand_tokens.py); the funnel resolves
// them here. Same inputs → same outputs.
//
// NOTE: contrast_safe text colors use contrastSafe() from
// themeEngine.js — the canonical source. This file must NOT
// evolve independent contrast logic.
// ============================================================

import { contrastSafe } from './themeEngine';

// ── Color Utilities ──────────────────────────────────────────
function hexToRgb(hex) {
  const h = hex.replace('#', '');
  const r = parseInt(h.substring(0, 2), 16);
  const g = parseInt(h.substring(2, 4), 16);
  const b = parseInt(h.substring(4, 6), 16);
  return { r, g, b };
}

function rgbStr(hex) {
  const { r, g, b } = hexToRgb(hex);
  return `${r},${g},${b}`;
}

function darken(hex, pct) {
  const { r, g, b } = hexToRgb(hex);
  const f = 1 - pct / 100;
  const nr = Math.round(r * f);
  const ng = Math.round(g * f);
  const nb = Math.round(b * f);
  return `#${nr.toString(16).padStart(2, '0')}${ng.toString(16).padStart(2, '0')}${nb.toString(16).padStart(2, '0')}`;
}

// ── Brand Config Schema ──────────────────────────────────────
// This is the minimal input that drives the entire visual system.
// Future: comes from DB brand settings. Currently derived from
// LP/funnel appearance data.
export const DEFAULT_BRAND = {
  accent: '#dc2626',
  surfaceMode: 'dark',  // 'dark' | 'light'
};

// ── Token Resolver ───────────────────────────────────────────
// Takes a small brand config, outputs a complete token map.
// Both LP (Python) and funnel (JS) produce identical outputs.
export function resolveThemeTokens(brand = {}) {
  const accent = brand.accent || DEFAULT_BRAND.accent;
  const mode = brand.surfaceMode || DEFAULT_BRAND.surfaceMode;
  const accentRgb = rgbStr(accent);
  const isDark = mode === 'dark';

  // Appearance overrides — when a funnel has specific custom hex colors
  const containerOverride = brand.containerBg;
  const threadOverride = brand.threadBg;
  const headerOverride = brand.headerBg;
  const bubbleAgentBgOverride = brand.bubbleAgentBg;
  const bubbleAgentTextOverride = brand.bubbleAgentText;
  const headerIconBgOverride = brand.headerIconBg;

  if (isDark) {
    return {
      // ── Page Shell ──
      pageBg: '#0b1220',
      surface1: '#0f172a',
      surface2: '#111827',
      surface3: '#0b1220',

      // ── Card ──
      cardBg: '#ffffff',
      cardBorder: '1px solid rgba(15,23,42,0.06)',
      cardShadowPrimary: '0 10px 30px rgba(0,0,0,0.08), 0 2px 8px rgba(0,0,0,0.04)',
      cardShadowSecondary: '0 4px 12px rgba(0,0,0,0.05)',

      // ── Text (on dark surfaces) ──
      textPrimary: 'rgba(255,255,255,0.92)',
      textSecondary: 'rgba(255,255,255,0.82)',
      textMuted: 'rgba(255,255,255,0.55)',
      textOnCard: '#0f172a',
      textOnCardSub: '#475569',
      textOnCardMuted: '#64748b',
      textInverse: '#ffffff',

      // ── Accent ──
      accent,
      accentStrong: darken(accent, 15),
      accentGlow: `rgba(${accentRgb},0.25)`,
      accentHover: `rgba(${accentRgb},0.85)`,

      // ── Funnel Header ──
      headerBg: headerOverride || '#111827',
      headerBorder: 'rgba(255,255,255,0.06)',
      headerTitleColor: 'rgba(255,255,255,0.85)',
      headerIconBg: headerIconBgOverride || `rgba(${accentRgb},0.20)`,
      headerIconColor: accent,

      // ── Message Surfaces (accent hierarchy) ──
      bubbleAgentBg: bubbleAgentBgOverride || 'rgba(255,255,255,0.06)',
      bubbleAgentText: bubbleAgentTextOverride || 'rgba(255,255,255,0.88)',
      bubbleUserBg: `rgba(${accentRgb},0.20)`,
      bubbleUserText: 'rgba(255,255,255,0.88)',
      // ── Response Controls ──
      optionBg: accent,
      optionText: contrastSafe(accent),
      optionHoverOpacity: '0.9',

      // ── Input ──
      inputBg: 'rgba(255,255,255,0.06)',
      inputText: 'rgba(255,255,255,0.9)',
      inputPlaceholder: 'rgba(255,255,255,0.35)',
      inputBorder: 'rgba(255,255,255,0.1)',
      inputFocusBorder: accent,
      inputFocusGlow: `0 0 0 3px rgba(${accentRgb},0.15)`,

      // ── Scroll Affordance ──
      scrollIndicatorBg: 'rgba(255,255,255,0.12)',
      scrollIndicatorBorder: '1px solid rgba(255,255,255,0.1)',
      scrollArrowColor: accent,
      gradientFadeRgb: '17,24,39',

      // ── Utility ──
      typingDotColor: 'rgba(255,255,255,0.5)',
      disabledBg: 'rgba(255,255,255,0.08)',
      skeletonBase: 'rgba(255,255,255,0.06)',
      skeletonPulse: 'rgba(255,255,255,0.12)',

      // ── Funnel Container ──
      containerBg: containerOverride || '#111827',
      threadBg: threadOverride || '#111827',

      // ── Compact Entry ──
      compactMessageBg: 'rgba(255,255,255,0.06)',
      compactMessageText: 'rgba(255,255,255,0.88)',

      // ── Shape ──
      radiusLg: '20px',
      radiusMd: '16px',
      radiusBtn: '12px',
      radiusPill: '100px',
      radiusBubbleAgent: '16px 16px 16px 4px',
      radiusBubbleUser: '16px 16px 4px 16px',

      // ── Spacing ──
      spaceXs: '8px',
      spaceSm: '14px',
      spaceMd: '20px',
      spaceLg: '28px',
      spaceXl: '40px',
      spaceSection: '72px',
      spaceCard: '28px',

      // ── Typography ──
      fontHeading: "-apple-system, BlinkMacSystemFont, 'SF Pro Display', 'Segoe UI', system-ui, sans-serif",
      fontBody: "-apple-system, BlinkMacSystemFont, 'SF Pro Text', 'Segoe UI', system-ui, sans-serif",

      // ── MA-TYPO-001: Typography Scale ──
      // Shared token-driven type system for LP + Funnel
      typo: {
        fontPrimary: "-apple-system, BlinkMacSystemFont, 'SF Pro Display', 'Segoe UI', system-ui, sans-serif",
        fontSecondary: "-apple-system, BlinkMacSystemFont, 'SF Pro Text', 'Segoe UI', system-ui, sans-serif",

        // Scale: [fontSize, lineHeight]
        displayLarge:   ['48px', '56px'],
        displayMedium:  ['40px', '48px'],
        headingLarge:   ['32px', '40px'],
        headingMedium:  ['24px', '32px'],
        headingSmall:   ['20px', '28px'],
        bodyLarge:      ['16px', '24px'],
        bodyMedium:     ['14px', '20px'],
        bodySmall:      ['12px', '18px'],
        label:          ['14px', '20px'],
        caption:        ['12px', '16px'],

        // Responsive overrides (mobile)
        displayLargeMobile:  ['32px', '40px'],
        displayMediumMobile: ['28px', '36px'],
        headingLargeMobile:  ['24px', '32px'],
        headingMediumMobile: ['20px', '28px'],

        // Weights
        weightRegular:  '400',
        weightMedium:   '500',
        weightSemibold: '600',
        weightBold:     '700',

        // Line heights (standalone)
        lineHeightDisplay: '1.17',
        lineHeightHeading: '1.25',
        lineHeightBody:    '1.5',

        // Tracking
        trackingTight:  '-0.02em',
        trackingNormal: '0em',
        trackingWide:   '0.02em',
      },

      // ── Meta ──
      surfaceMode: 'dark',
    };
  }

  // ── LIGHT MODE ──
  return {
    pageBg: '#ffffff',
    surface1: '#f9fafb',
    surface2: '#f3f4f6',
    surface3: '#ffffff',

    cardBg: '#ffffff',
    cardBorder: '1px solid #e5e7eb',
    cardShadowPrimary: '0 10px 30px rgba(0,0,0,0.06), 0 2px 8px rgba(0,0,0,0.03)',
    cardShadowSecondary: '0 4px 12px rgba(0,0,0,0.04)',

    textPrimary: '#1C1C1E',
    textSecondary: '#475569',
    textMuted: '#8E8E93',
    textOnCard: '#1C1C1E',
    textOnCardSub: '#475569',
    textOnCardMuted: '#8E8E93',
    textInverse: '#ffffff',

    accent,
    accentStrong: darken(accent, 15),
    accentGlow: `rgba(${accentRgb},0.15)`,
    accentHover: `rgba(${accentRgb},0.85)`,

    headerBg: headerOverride || '#ffffff',
    headerBorder: '#e5e7eb',
    headerTitleColor: '#1C1C1E',
    headerIconBg: headerIconBgOverride || `rgba(${accentRgb},0.12)`,
    headerIconColor: accent,

    bubbleAgentBg: bubbleAgentBgOverride || '#E9E9EB',
    bubbleAgentText: bubbleAgentTextOverride || '#1C1C1E',
    bubbleUserBg: `rgba(${accentRgb},0.12)`,
    bubbleUserText: '#1C1C1E',

    optionBg: accent,
    optionText: contrastSafe(accent),
    optionHoverOpacity: '0.9',

    inputBg: '#F2F2F7',
    inputText: '#1C1C1E',
    inputPlaceholder: '#8E8E93',
    inputBorder: 'transparent',
    inputFocusBorder: accent,
    inputFocusGlow: `0 0 0 3px rgba(${accentRgb},0.1)`,

    scrollIndicatorBg: '#ffffff',
    scrollIndicatorBorder: 'none',
    scrollArrowColor: accent,
    gradientFadeRgb: '250,250,250',

    typingDotColor: '#8E8E93',
    disabledBg: '#E5E5EA',
    skeletonBase: '#f3f4f6',
    skeletonPulse: '#e5e7eb',

    containerBg: containerOverride || '#FFFFFF',
    threadBg: threadOverride || '#FFFFFF',

    compactMessageBg: '#E9E9EB',
    compactMessageText: '#1C1C1E',

    radiusLg: '20px',
    radiusMd: '16px',
    radiusBtn: '12px',
    radiusPill: '100px',
    radiusBubbleAgent: '16px 16px 16px 4px',
    radiusBubbleUser: '16px 16px 4px 16px',

    spaceXs: '8px',
    spaceSm: '14px',
    spaceMd: '20px',
    spaceLg: '28px',
    spaceXl: '40px',
    spaceSection: '72px',
    spaceCard: '28px',

    fontHeading: "-apple-system, BlinkMacSystemFont, 'SF Pro Display', 'Segoe UI', system-ui, sans-serif",
    fontBody: "-apple-system, BlinkMacSystemFont, 'SF Pro Text', 'Segoe UI', system-ui, sans-serif",

    // ── MA-TYPO-001: Typography Scale ──
    typo: {
      fontPrimary: "-apple-system, BlinkMacSystemFont, 'SF Pro Display', 'Segoe UI', system-ui, sans-serif",
      fontSecondary: "-apple-system, BlinkMacSystemFont, 'SF Pro Text', 'Segoe UI', system-ui, sans-serif",

      displayLarge:   ['48px', '56px'],
      displayMedium:  ['40px', '48px'],
      headingLarge:   ['32px', '40px'],
      headingMedium:  ['24px', '32px'],
      headingSmall:   ['20px', '28px'],
      bodyLarge:      ['16px', '24px'],
      bodyMedium:     ['14px', '20px'],
      bodySmall:      ['12px', '18px'],
      label:          ['14px', '20px'],
      caption:        ['12px', '16px'],

      displayLargeMobile:  ['32px', '40px'],
      displayMediumMobile: ['28px', '36px'],
      headingLargeMobile:  ['24px', '32px'],
      headingMediumMobile: ['20px', '28px'],

      weightRegular:  '400',
      weightMedium:   '500',
      weightSemibold: '600',
      weightBold:     '700',

      lineHeightDisplay: '1.17',
      lineHeightHeading: '1.25',
      lineHeightBody:    '1.5',

      trackingTight:  '-0.02em',
      trackingNormal: '0em',
      trackingWide:   '0.02em',
    },

    surfaceMode: 'light',
  };
}

// ── Derive brand config from URL params + appearance ─────────
// Used by ConversationFunnelEmbed to pass brand tokens to funnel
export function deriveBrandFromParams(searchParams, appearance) {
  const accent = searchParams.get('brand_accent') || appearance?.prospectColor || DEFAULT_BRAND.accent;
  const surfaceMode = searchParams.get('theme') === 'lp_dark' ? 'dark' : 'light';
  return { accent, surfaceMode };
}

// ── MA-TYPO-001: Generate CSS custom properties for typography ──
// Produces a CSS string of --ma-typo-* variables from resolved tokens.
// Injected at root/wrapper level alongside color token variables.
export function generateTypographyCSSVars(typo) {
  if (!typo) return '';
  const v = (name, val) => `--ma-typo-${name}: ${val};`;
  const scaleVar = (name, pair) => {
    if (!Array.isArray(pair)) return '';
    return `${v(`${name}-size`, pair[0])}\n      ${v(`${name}-lh`, pair[1])}`;
  };

  return `
      /* MA-TYPO-001: Typography Token Layer */
      ${v('font-primary', typo.fontPrimary)}
      ${v('font-secondary', typo.fontSecondary)}

      /* Scale */
      ${scaleVar('display-lg', typo.displayLarge)}
      ${scaleVar('display-md', typo.displayMedium)}
      ${scaleVar('heading-lg', typo.headingLarge)}
      ${scaleVar('heading-md', typo.headingMedium)}
      ${scaleVar('heading-sm', typo.headingSmall)}
      ${scaleVar('body-lg', typo.bodyLarge)}
      ${scaleVar('body-md', typo.bodyMedium)}
      ${scaleVar('body-sm', typo.bodySmall)}
      ${scaleVar('label', typo.label)}
      ${scaleVar('caption', typo.caption)}

      /* Weights */
      ${v('weight-regular', typo.weightRegular)}
      ${v('weight-medium', typo.weightMedium)}
      ${v('weight-semibold', typo.weightSemibold)}
      ${v('weight-bold', typo.weightBold)}

      /* Tracking */
      ${v('tracking-tight', typo.trackingTight)}
      ${v('tracking-normal', typo.trackingNormal)}
      ${v('tracking-wide', typo.trackingWide)}
  `;
}

// Responsive overrides for mobile (max-width: 768px)
export function generateTypographyMobileCSSVars(typo) {
  if (!typo) return '';
  const v = (name, val) => `--ma-typo-${name}: ${val};`;
  const scaleVar = (name, pair) => {
    if (!Array.isArray(pair)) return '';
    return `${v(`${name}-size`, pair[0])}\n        ${v(`${name}-lh`, pair[1])}`;
  };

  return `
    @media (max-width: 768px) {
      .ma-typo-root {
        ${scaleVar('display-lg', typo.displayLargeMobile)}
        ${scaleVar('display-md', typo.displayMediumMobile)}
        ${scaleVar('heading-lg', typo.headingLargeMobile)}
        ${scaleVar('heading-md', typo.headingMediumMobile)}
      }
    }
  `;
}

